import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { Observable } from 'rxjs';
import { APP_CONFIG } from '../../config/environment.token';
import { Environment } from '../../../../environments/environment.model';
import {
  ApiResponse,
  PaginatedApiResponse,
} from '../../../shared/types/api.type';
import { Task, TaskFilters, TaskStatus, TaskType } from './types/task.type';

@Injectable({ providedIn: 'root' })
export class TasksService {
  private readonly http = inject(HttpClient);
  private readonly appConfig = inject(APP_CONFIG) as Environment;
  private readonly baseUrl = `${this.appConfig.apiUrl}/tasks`;

  readonly pendingCount = signal<number>(0);

  refreshPendingCount(): void {
    this.countPending().subscribe({
      next: response => this.pendingCount.set(response.data?.count ?? 0),
      error: () => this.pendingCount.set(0),
    });
  }

  list(
    page: number,
    limit: number,
    filters: TaskFilters = {},
  ): Observable<PaginatedApiResponse<Task>> {
    let params = new HttpParams()
      .set('page', String(page))
      .set('limit', String(limit));

    if (filters.typeId) {
      params = params.set('typeId', String(filters.typeId));
    }
    if (filters.groupId) {
      params = params.set('groupId', String(filters.groupId));
    }
    if (filters.studentId) {
      params = params.set('studentId', String(filters.studentId));
    }
    if (filters.status) {
      params = params.set('status', filters.status);
    }
    if (filters.studentSearch) {
      params = params.set('studentSearch', filters.studentSearch);
    }
    if (filters.phoneSearch) {
      params = params.set('phoneSearch', filters.phoneSearch);
    }
    filters.universities?.forEach(id => {
      params = params.append('universities', id.toString());
    });
    filters.audiences?.forEach(audience => {
      params = params.append('audiences', audience);
    });
    filters.origins?.forEach(origin => {
      params = params.append('origins', origin);
    });
    if (filters.groupSearch) {
      params = params.set('groupSearch', filters.groupSearch);
    }
    if (filters.dateFrom) {
      const dateFrom = new Date(filters.dateFrom);
      dateFrom.setHours(0, 0, 0, 0);
      params = params.set('dateFrom', dateFrom.toISOString());
    }
    if (filters.dateTo) {
      const dateTo = new Date(filters.dateTo);
      dateTo.setHours(23, 59, 59, 999);
      params = params.set('dateTo', dateTo.toISOString());
    }

    return this.http.get<PaginatedApiResponse<Task>>(this.baseUrl, { params });
  }

  listTypes(): Observable<ApiResponse<TaskType[]>> {
    return this.http.get<ApiResponse<TaskType[]>>(`${this.baseUrl}/types`);
  }

  countPending(groupId?: number): Observable<ApiResponse<{ count: number }>> {
    const params = groupId
      ? new HttpParams().set('groupId', String(groupId))
      : undefined;

    return this.http.get<ApiResponse<{ count: number }>>(
      `${this.baseUrl}/pending-count`,
      { params },
    );
  }

  markDone(id: number): Observable<ApiResponse<Task>> {
    return this.http.patch<ApiResponse<Task>>(
      `${this.baseUrl}/${id}/mark-done`,
      {},
    );
  }

  setStatus(ids: number[], status: TaskStatus): Observable<ApiResponse<Task[]>> {
    return this.http.patch<ApiResponse<Task[]>>(`${this.baseUrl}/status`, {
      ids,
      status,
    });
  }
}
